import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  adminService,
  InsurerOrg,
  InsurerOrgDetail,
  PartnerLedgerAdminEntry,
} from '../../services/adminService';
import { useAdminHeaderActions } from '../../contexts/AdminHeaderActionsContext';
import { usePartnerTokenTiers } from '../../contexts/PartnerTokenTiersContext';
import ManagePartnerTokensModal from './ManagePartnerTokensModal';
import RevokeTokenModal, { RevokeTargetClient } from '../Insurer/RevokeTokenModal';

type OrgFilter = 'pending' | 'active' | 'suspended' | 'rejected' | 'all';
type LedgerStatusFilter = 'all' | 'pending_invoice' | 'invoiced' | 'paid' | 'void';

const TOKEN_TIER_LETTERS: Record<'simple' | 'gold' | 'premium', string> = {
  simple: 'S',
  gold: 'G',
  premium: 'P',
};

const TOKEN_TIER_TITLES: Record<'simple' | 'gold' | 'premium', string> = {
  simple: 'Prepared',
  gold: 'Prepared+',
  premium: 'Executive',
};

const TEAM_SECTION_EXPANDED_KEY = 'flashai.admin.insurerDetail.teamExpanded';
const CLIENTS_SECTION_EXPANDED_KEY = 'flashai.admin.insurerDetail.clientsExpanded';

const readStoredExpanded = (key: string): boolean => {
  try {
    const stored = localStorage.getItem(key);
    if (stored === null) return true;
    return stored === 'true';
  } catch {
    return true;
  }
};

const InsurerManagement: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setActions } = useAdminHeaderActions();
  const { tokenTitles, tokenLetters } = usePartnerTokenTiers();

  // Modals & Navigation
  const [activeSubTab, setActiveSubTab] = useState<'ledger' | 'organizations'>('ledger');
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<{ orgId: number; client: RevokeTargetClient } | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [orgStatusConfirm, setOrgStatusConfirm] = useState<{
    open: boolean;
    orgId: number | null;
    orgName: string;
    status: 'suspended' | 'active' | null;
  }>({ open: false, orgId: null, orgName: '', status: null });

  // Data
  const [insurers, setInsurers] = useState<InsurerOrg[]>([]);
  const [allOrgs, setAllOrgs] = useState<InsurerOrg[]>([]);
  const [ledger, setLedger] = useState<PartnerLedgerAdminEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<LedgerStatusFilter>('pending_invoice');
  const [selectedOrgFilterId, setSelectedOrgFilterId] = useState<number | ''>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [orgFilter, setOrgFilter] = useState<OrgFilter>('active');
  const [selectedLedgerIds, setSelectedLedgerIds] = useState<Set<number>>(new Set());
  const [showAll, setShowAll] = useState(false);

  // Detail View
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [orgDetail, setOrgDetail] = useState<InsurerOrgDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    billing_email: '',
    invite_contact_email: '',
  });
  const [editSaving, setEditSaving] = useState(false);
  const [clientActionId, setClientActionId] = useState<number | null>(null);
  const [actionId, setActionId] = useState<number | null>(null);
  const [ledgerAction, setLedgerAction] = useState<{
    id: number;
    status: string;
    invoice_reference: string;
    note: string;
  } | null>(null);
  const [teamExpanded, setTeamExpanded] = useState(() => readStoredExpanded(TEAM_SECTION_EXPANDED_KEY));
  const [clientsExpanded, setClientsExpanded] = useState(() => readStoredExpanded(CLIENTS_SECTION_EXPANDED_KEY));

  // Create Form State
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    org_name: '',
    billing_email: '',
    admin_email: '',
    admin_password: '',
    admin_full_name: '',
    auto_approve: true,
    use_magic_link: true,
  });

  // Keep top navbar clear
  useEffect(() => {
    setActions(null);
    return () => setActions(null);
  }, [setActions]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [orgs, ledgerData, allOrgsData] = await Promise.all([
        adminService.listInsurerOrgs(orgFilter === 'all' ? undefined : orgFilter),
        adminService.listPartnerLedger(),
        adminService.listInsurerOrgs(),
      ]);
      setInsurers(orgs.insurers);
      setLedger(ledgerData.entries);
      setAllOrgs(allOrgsData.insurers);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load insurer data');
    } finally {
      setLoading(false);
    }
  }, [orgFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    try {
      localStorage.setItem(TEAM_SECTION_EXPANDED_KEY, String(teamExpanded));
    } catch {}
  }, [teamExpanded]);

  useEffect(() => {
    try {
      localStorage.setItem(CLIENTS_SECTION_EXPANDED_KEY, String(clientsExpanded));
    } catch {}
  }, [clientsExpanded]);

  useEffect(() => {
    if (searchParams.get('create') === '1') {
      setShowCreateForm(true);
      const next = new URLSearchParams(searchParams);
      next.delete('create');
      setSearchParams(next, { replace: true });
    } else if (searchParams.get('tokens') === '1') {
      setShowTokenModal(true);
      const next = new URLSearchParams(searchParams);
      next.delete('tokens');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadOrgDetail = useCallback(async (orgId: number) => {
    setDetailLoading(true);
    setError(null);
    try {
      const detail = await adminService.getInsurerOrgDetail(orgId);
      setOrgDetail(detail);
      setSelectedOrgId(orgId);
      setEditForm({
        name: detail.org.name,
        billing_email: detail.org.billing_email || '',
        invite_contact_email: detail.org.invite_contact_email || '',
      });
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load organization detail');
      setOrgDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const saveOrgEdit = async () => {
    if (!selectedOrgId) return;
    setEditSaving(true);
    setError(null);
    try {
      await adminService.patchInsurerOrg(selectedOrgId, {
        name: editForm.name.trim(),
        billing_email: editForm.billing_email.trim() || undefined,
        invite_contact_email: editForm.invite_contact_email.trim() || undefined,
      });
      setMessage('Organization updated.');
      await loadOrgDetail(selectedOrgId);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to update organization');
    } finally {
      setEditSaving(false);
    }
  };

  const approve = async (orgId: number) => {
    setActionId(orgId);
    setMessage(null);
    setError(null);
    try {
      await adminService.approveInsurerOrg(orgId);
      setMessage('Organization approved.');
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Approval failed');
    } finally {
      setActionId(null);
    }
  };

  const reject = async (orgId: number) => {
    setActionId(orgId);
    setMessage(null);
    setError(null);
    try {
      await adminService.rejectInsurerOrg(orgId, rejectReason.trim() || undefined);
      setMessage('Application rejected and applicant notified.');
      setRejectingId(null);
      setRejectReason('');
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Rejection failed');
    } finally {
      setActionId(null);
    }
  };

  const setOrgStatus = async (orgId: number, status: string) => {
    setActionId(orgId);
    setMessage(null);
    setError(null);
    try {
      await adminService.updateInsurerOrgStatus(orgId, status);
      setMessage(`Organization ${status === 'suspended' ? 'suspended' : 'reactivated'}.`);
      setOrgStatusConfirm({ open: false, orgId: null, orgName: '', status: null });
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Status update failed');
    } finally {
      setActionId(null);
    }
  };

  const handleOpenOrgStatusConfirm = (
    orgId: number,
    orgName: string,
    status: 'suspended' | 'active'
  ) => {
    setOrgStatusConfirm({ open: true, orgId, orgName, status });
  };

  const handleConfirmOrgStatus = async () => {
    if (!orgStatusConfirm.orgId || !orgStatusConfirm.status) return;
    await setOrgStatus(orgStatusConfirm.orgId, orgStatusConfirm.status);
  };

  const createInsurerOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setMessage(null);
    setError(null);
    try {
      const result = await adminService.createInsurerOrg({
        org_name: createForm.org_name.trim(),
        billing_email: createForm.billing_email.trim(),
        admin_email: createForm.admin_email.trim(),
        admin_password: createForm.use_magic_link ? undefined : createForm.admin_password,
        admin_full_name: createForm.admin_full_name.trim() || undefined,
        auto_approve: createForm.auto_approve,
        use_magic_link: createForm.use_magic_link,
      });
      const activeMessage = createForm.use_magic_link
        ? `Created and activated ${result.org.name}. A welcome email with a set-password link was sent to ${result.admin_email}.`
        : `Created and activated ${result.org.name}. Admin can log in at ${result.admin_email}.`;
      setMessage(
        result.org.status === 'active'
          ? activeMessage
          : `Created ${result.org.name} as pending. Approve it when ready.`
      );
      setShowCreateForm(false);
      setCreateForm({
        org_name: '',
        billing_email: '',
        admin_email: '',
        admin_password: '',
        admin_full_name: '',
        auto_approve: true,
        use_magic_link: true,
      });
      setOrgFilter(result.org.status === 'active' ? 'active' : 'pending');
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to create insurer organization');
    } finally {
      setCreating(false);
    }
  };

  const updateLedgerStatus = async (
    ledgerId: number,
    status: string,
    invoiceReference?: string,
    note?: string
  ) => {
    setMessage(null);
    setError(null);
    try {
      await adminService.updatePartnerLedgerStatus(ledgerId, {
        status,
        invoice_reference: invoiceReference,
        note,
      });
      setMessage(`Ledger entry updated.`);
      setLedgerAction(null);
      await load();
      if (selectedOrgId) {
        await loadOrgDetail(selectedOrgId);
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Update failed');
    }
  };

  const grantClientCredit = async (orgId: number, clientId: number) => {
    if (!window.confirm('Grant one tabletop credit to this client?')) return;
    setClientActionId(clientId);
    setMessage(null);
    setError(null);
    try {
      await adminService.grantClientCredit(orgId, clientId);
      setMessage('Credit granted.');
      await loadOrgDetail(orgId);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Grant failed');
    } finally {
      setClientActionId(null);
    }
  };

  const submitLedgerAction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ledgerAction) return;
    await updateLedgerStatus(
      ledgerAction.id,
      ledgerAction.status,
      ledgerAction.invoice_reference.trim() || undefined,
      ledgerAction.note.trim() || undefined
    );
  };

  const formatCents = (cents: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

  // 4 Metric Card Stats
  const metrics = useMemo(() => {
    const pendingEntries = ledger.filter((e) => e.status === 'pending_invoice');
    const invoicedEntries = ledger.filter((e) => e.status === 'invoiced');
    const paidEntries = ledger.filter((e) => e.status === 'paid');
    const voidEntries = ledger.filter((e) => e.status === 'void');

    return {
      pending: {
        cents: pendingEntries.reduce((sum, e) => sum + e.amount_cents, 0),
        count: pendingEntries.length,
      },
      invoiced: {
        cents: invoicedEntries.reduce((sum, e) => sum + e.amount_cents, 0),
        count: invoicedEntries.length,
      },
      paid: {
        cents: paidEntries.reduce((sum, e) => sum + e.amount_cents, 0),
        count: paidEntries.length,
      },
      void: {
        cents: voidEntries.reduce((sum, e) => sum + e.amount_cents, 0),
        count: voidEntries.length,
      },
    };
  }, [ledger]);

  // Map each insurer org to their pending cents for filter dropdown
  const insurerPills = useMemo(() => {
    const orgMap = new Map<number, { name: string; pendingCents: number }>();
    allOrgs.forEach((org) => {
      orgMap.set(org.id, { name: org.name, pendingCents: 0 });
    });
    ledger.forEach((e) => {
      if (e.status === 'pending_invoice') {
        const existing = orgMap.get(e.insurer_org_id);
        if (existing) {
          existing.pendingCents += e.amount_cents;
        } else {
          orgMap.set(e.insurer_org_id, { name: e.insurer_name, pendingCents: e.amount_cents });
        }
      }
    });
    return Array.from(orgMap.entries())
      .map(([id, info]) => ({
        id,
        name: info.name,
        pendingCents: info.pendingCents,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allOrgs, ledger]);

  // Filtered Ledger list
  const filteredLedger = useMemo(() => {
    return ledger.filter((entry) => {
      if (statusFilter !== 'all' && entry.status !== statusFilter) {
        return false;
      }
      if (selectedOrgFilterId !== '' && entry.insurer_org_id !== selectedOrgFilterId) {
        return false;
      }
      if (startDate) {
        const entryDate = new Date(entry.created_at);
        const start = new Date(`${startDate}T00:00:00`);
        if (entryDate < start) {
          return false;
        }
      }
      if (endDate) {
        const entryDate = new Date(entry.created_at);
        const end = new Date(`${endDate}T23:59:59.999`);
        if (entryDate > end) {
          return false;
        }
      }
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const matchClient =
          (entry.client_email || '').toLowerCase().includes(query) ||
          (entry.client_name || '').toLowerCase().includes(query);
        const matchInsurer = (entry.insurer_name || '').toLowerCase().includes(query);
        const matchInvoice = (entry.invoice_reference || '').toLowerCase().includes(query);
        if (!matchClient && !matchInsurer && !matchInvoice) {
          return false;
        }
      }
      return true;
    });
  }, [ledger, statusFilter, selectedOrgFilterId, startDate, endDate, searchQuery]);

  // Reset selected rows when filter criteria change
  useEffect(() => {
    setSelectedLedgerIds(new Set());
  }, [statusFilter, selectedOrgFilterId, startDate, endDate, searchQuery]);

  const displayedLedger = useMemo(() => {
    if (showAll) return filteredLedger;
    return filteredLedger.slice(0, 10);
  }, [filteredLedger, showAll]);

  const handleToggleSelectAll = () => {
    if (selectedLedgerIds.size === displayedLedger.length && displayedLedger.length > 0) {
      setSelectedLedgerIds(new Set());
    } else {
      setSelectedLedgerIds(new Set(displayedLedger.map((e) => e.id)));
    }
  };

  const handleToggleSelectRow = (id: number) => {
    const next = new Set(selectedLedgerIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedLedgerIds(next);
  };

  if (loading && insurers.length === 0 && ledger.length === 0) {
    return (
      <div className="flex justify-center items-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0c1e40]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800 shadow-sm flex items-center justify-between">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} className="text-red-500 font-bold ml-4">
            &times;
          </button>
        </div>
      )}
      {message && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-800 shadow-sm flex items-center justify-between">
          <span>{message}</span>
          <button type="button" onClick={() => setMessage(null)} className="text-emerald-500 font-bold ml-4">
            &times;
          </button>
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-[#0c1e40] tracking-tight">Insurer partners</h1>
          <p className="text-sm text-gray-500 mt-1">
            Partner organizations, and the credits Flash invoices them for.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setShowTokenModal(true)}
            className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 bg-white text-[#0c1e40] hover:bg-gray-50 shadow-sm transition-colors"
          >
            Token names &amp; tiers
          </button>
          <button
            type="button"
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-[#991b1b] text-white hover:bg-[#7f1d1d] shadow-sm transition-colors"
          >
            <span className="text-base font-bold leading-none">+</span> Onboard insurer
          </button>
        </div>
      </div>

      {/* 4-Metric Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Pending invoice */}
        <div
          onClick={() => setStatusFilter(statusFilter === 'pending_invoice' ? 'all' : 'pending_invoice')}
          className={`bg-white rounded-xl border p-5 shadow-sm cursor-pointer transition-all ${
            statusFilter === 'pending_invoice'
              ? 'border-gray-200 border-b-2 border-b-[#991b1b]'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
            <span className="w-2 h-2 rounded-full bg-[#ef4444]" />
            <span>Pending invoice</span>
          </div>
          <div className="text-3xl font-extrabold text-[#0c1e40] mt-2 mb-1">
            {formatCents(metrics.pending.cents)}
          </div>
          <div className="text-xs text-gray-500">{metrics.pending.count} credits</div>
        </div>

        {/* Card 2: Invoiced */}
        <div
          onClick={() => setStatusFilter(statusFilter === 'invoiced' ? 'all' : 'invoiced')}
          className={`bg-white rounded-xl border p-5 shadow-sm cursor-pointer transition-all ${
            statusFilter === 'invoiced'
              ? 'border-gray-200 border-b-2 border-b-[#991b1b]'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
            <span className="w-2 h-2 rounded-full bg-[#f59e0b]" />
            <span>Invoiced</span>
          </div>
          <div className="text-3xl font-extrabold text-[#0c1e40] mt-2 mb-1">
            {formatCents(metrics.invoiced.cents)}
          </div>
          <div className="text-xs text-gray-500">{metrics.invoiced.count} credits</div>
        </div>

        {/* Card 3: Paid */}
        <div
          onClick={() => setStatusFilter(statusFilter === 'paid' ? 'all' : 'paid')}
          className={`bg-white rounded-xl border p-5 shadow-sm cursor-pointer transition-all ${
            statusFilter === 'paid'
              ? 'border-gray-200 border-b-2 border-b-[#991b1b]'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
            <span className="w-2 h-2 rounded-full bg-[#10b981]" />
            <span>Paid</span>
          </div>
          <div className="text-3xl font-extrabold text-[#0c1e40] mt-2 mb-1">
            {formatCents(metrics.paid.cents)}
          </div>
          <div className="text-xs text-gray-500">{metrics.paid.count} credits</div>
        </div>

        {/* Card 4: Void */}
        <div
          onClick={() => setStatusFilter(statusFilter === 'void' ? 'all' : 'void')}
          className={`bg-white rounded-xl border p-5 shadow-sm cursor-pointer transition-all ${
            statusFilter === 'void'
              ? 'border-gray-200 border-b-2 border-b-[#991b1b]'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-600">
            <span className="w-2 h-2 rounded-full bg-[#64748b]" />
            <span>Void</span>
          </div>
          <div className="text-3xl font-extrabold text-[#0c1e40] mt-2 mb-1">
            {formatCents(metrics.void.cents)}
          </div>
          <div className="text-xs text-gray-500">{metrics.void.count} credits</div>
        </div>
      </div>

      {/* Sub-Tabs: Credit ledger vs Organizations */}
      <div className="flex items-center gap-8 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setActiveSubTab('ledger')}
          className={`pb-3 text-sm font-bold flex items-center gap-2 transition-all relative ${
            activeSubTab === 'ledger'
              ? 'text-[#0c1e40] border-b-2 border-[#991b1b]'
              : 'text-gray-500 hover:text-gray-800'
          }`}
        >
          <span>Credit ledger</span>
          <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-[#0c1e40] text-white">
            {ledger.length}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveSubTab('organizations')}
          className={`pb-3 text-sm font-semibold flex items-center gap-2 transition-all relative ${
            activeSubTab === 'organizations'
              ? 'text-[#0c1e40] font-bold border-b-2 border-[#991b1b]'
              : 'text-gray-500 hover:text-gray-800'
          }`}
        >
          <span>Organizations</span>
          <span className="text-xs text-gray-500 font-normal">
            {allOrgs.length}
          </span>
        </button>
      </div>

      {/* ───────────── TAB 1: CREDIT LEDGER (SCREENSHOT 7) ───────────── */}
      {activeSubTab === 'ledger' && (
        <div className="space-y-6">
          {/* Credit Ledger Search & Filter Bar */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3.5 items-end">
              {/* Insurer Dropdown */}
              <div className="lg:col-span-4">
                <label htmlFor="insurer-dropdown-filter" className="block text-xs font-semibold text-gray-700 mb-1">
                  Insurer partner
                </label>
                <div className="relative">
                  <select
                    id="insurer-dropdown-filter"
                    aria-label="Filter by insurer"
                    value={selectedOrgFilterId}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedOrgFilterId(val === '' ? '' : Number(val));
                    }}
                    className="w-full pl-3 pr-8 py-2 text-xs font-medium rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#0c1e40] focus:border-[#0c1e40] transition-colors appearance-none cursor-pointer"
                  >
                    <option value="">All Insurers ({allOrgs.length})</option>
                    {insurerPills.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}{org.pendingCents > 0 ? ` (${formatCents(org.pendingCents)} pending)` : ''}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2.5 text-gray-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* From Date */}
              <div className="lg:col-span-2">
                <label htmlFor="ledger-from-date" className="block text-xs font-semibold text-gray-700 mb-1">
                  From
                </label>
                <input
                  id="ledger-from-date"
                  type="date"
                  aria-label="Filter from date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 text-xs font-medium rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#0c1e40] focus:border-[#0c1e40] transition-colors cursor-pointer"
                />
              </div>

              {/* To Date */}
              <div className="lg:col-span-2">
                <label htmlFor="ledger-to-date" className="block text-xs font-semibold text-gray-700 mb-1">
                  To
                </label>
                <input
                  id="ledger-to-date"
                  type="date"
                  aria-label="Filter to date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 text-xs font-medium rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#0c1e40] focus:border-[#0c1e40] transition-colors cursor-pointer"
                />
              </div>

              {/* Search Query */}
              <div className="lg:col-span-3">
                <label htmlFor="ledger-search-query" className="block text-xs font-semibold text-gray-700 mb-1">
                  Search
                </label>
                <div className="relative">
                  <input
                    id="ledger-search-query"
                    type="text"
                    aria-label="Search ledger entries"
                    placeholder="Search client email, invoice ref..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 text-xs font-medium rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0c1e40] focus:border-[#0c1e40] transition-colors"
                  />
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5 text-gray-400">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Reset/Clear Button */}
              <div className="lg:col-span-1 flex items-end">
                {Boolean(selectedOrgFilterId !== '' || startDate !== '' || endDate !== '' || searchQuery.trim() !== '') ? (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedOrgFilterId('');
                      setStartDate('');
                      setEndDate('');
                      setSearchQuery('');
                    }}
                    title="Clear all filters"
                    className="w-full py-2 px-2 text-xs font-semibold text-slate-600 hover:text-red-600 bg-slate-50 hover:bg-red-50 border border-slate-200 hover:border-red-200 rounded-lg transition-colors text-center cursor-pointer"
                  >
                    Reset
                  </button>
                ) : (
                  <div className="h-[34px]" />
                )}
              </div>
            </div>
          </div>

          {/* Table Summary & Export */}
          <div className="flex items-center justify-between gap-4">
            <div className="text-sm font-medium text-gray-600">
              <span className="font-semibold text-gray-900">{filteredLedger.length}</span>{' '}
              {statusFilter === 'pending_invoice'
                ? 'pending invoice credits'
                : 'credits'}{' '}
              &bull;{' '}
              <span className="font-semibold text-gray-900">
                {formatCents(filteredLedger.reduce((sum, e) => sum + e.amount_cents, 0))}
              </span>
            </div>
            <button
              type="button"
              onClick={() =>
                adminService.downloadPartnerLedgerCsv(
                  selectedOrgFilterId === '' ? undefined : selectedOrgFilterId,
                  startDate || undefined,
                  endDate || undefined
                )
              }
              className="px-4 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 shadow-sm transition-colors"
            >
              Export CSV
            </button>
          </div>

          {/* Credit Ledger Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-white border-b border-gray-200">
                  <tr>
                    <th className="w-10 px-4 py-3.5 text-left">
                      <input
                        type="checkbox"
                        checked={
                          selectedLedgerIds.size === displayedLedger.length && displayedLedger.length > 0
                        }
                        onChange={handleToggleSelectAll}
                        className="w-4 h-4 rounded border-gray-300 text-[#0c1e40] focus:ring-[#0c1e40]"
                      />
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Insurer
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3.5 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Tier
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Next Step
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {displayedLedger.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center text-sm text-gray-500">
                        No credit ledger records found matching the selected filter.
                      </td>
                    </tr>
                  ) : (
                    displayedLedger.map((entry) => {
                      const tier = entry.partner_token_tier;
                      const letter = tier
                        ? tokenLetters[tier] ||
                          TOKEN_TIER_LETTERS[tier as 'simple' | 'gold' | 'premium'] ||
                          tier[0].toUpperCase()
                        : null;
                      const title = tier
                        ? tokenTitles[tier] ||
                          TOKEN_TIER_TITLES[tier as 'simple' | 'gold' | 'premium'] ||
                          tier
                        : undefined;
                      const isSelected = selectedLedgerIds.has(entry.id);

                      return (
                        <tr
                          key={entry.id}
                          className={`hover:bg-slate-50/70 transition-colors ${
                            isSelected ? 'bg-blue-50/30' : ''
                          }`}
                        >
                          <td className="w-10 px-4 py-4">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleSelectRow(entry.id)}
                              className="w-4 h-4 rounded border-gray-300 text-[#0c1e40] focus:ring-[#0c1e40]"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-xs font-medium text-gray-700">
                              {entry.created_at
                                ? new Date(entry.created_at).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                  })
                                : '—'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-bold text-[#0c1e40]">{entry.insurer_name}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-600">{entry.client_email}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {letter ? (
                              <span
                                className="inline-flex w-6 h-6 items-center justify-center rounded bg-[#e0e7ff] text-xs font-bold text-[#1e40af]"
                                title={title}
                              >
                                {letter}
                              </span>
                            ) : (
                              <span className="text-gray-400 font-bold">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-bold text-[#0c1e40]">
                              {formatCents(entry.amount_cents)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {entry.status === 'pending_invoice' ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#fef2f2] text-[#991b1b] border border-red-100">
                                Pending invoice
                              </span>
                            ) : entry.status === 'invoiced' ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100">
                                Invoiced
                              </span>
                            ) : entry.status === 'paid' ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                                Paid
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                Void
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            {entry.status === 'pending_invoice' && (
                              <button
                                type="button"
                                onClick={() => updateLedgerStatus(entry.id, 'invoiced')}
                                className="px-3 py-1 text-xs font-semibold rounded-lg border border-[#0c1e40] bg-white text-[#0c1e40] hover:bg-slate-50 transition-colors shadow-xs"
                              >
                                Mark invoiced
                              </button>
                            )}
                            {entry.status === 'invoiced' && (
                              <button
                                type="button"
                                onClick={() => updateLedgerStatus(entry.id, 'paid')}
                                className="px-3 py-1 text-xs font-semibold rounded-lg border border-emerald-600 bg-white text-emerald-700 hover:bg-emerald-50 transition-colors shadow-xs"
                              >
                                Mark paid
                              </button>
                            )}
                            {entry.status === 'paid' && (
                              <span className="text-xs text-emerald-600 font-semibold">Completed</span>
                            )}
                            {entry.status === 'void' && (
                              <span className="text-xs text-gray-400">Voided</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Table Footer */}
            <div className="p-4 border-t border-gray-200 bg-white flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
              <span className="text-gray-500 font-medium">
                Showing {Math.min(displayedLedger.length, filteredLedger.length)} of {filteredLedger.length}
              </span>

              <span className="text-gray-500">
                Tiers P, S, G are defined in{' '}
                <button
                  type="button"
                  onClick={() => setShowTokenModal(true)}
                  className="text-blue-600 hover:underline font-semibold"
                >
                  Token names &amp; tiers
                </button>
              </span>

              <button
                type="button"
                onClick={() => setShowAll(!showAll)}
                className="px-4 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 shadow-sm transition-colors"
              >
                {showAll ? 'Show 10' : 'Show all'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ───────────── TAB 2: ORGANIZATIONS ───────────── */}
      {activeSubTab === 'organizations' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
            <div>
              <h2 className="text-lg font-bold text-[#0c1e40]">Partner Organizations</h2>
              <p className="text-xs text-gray-500">Review status, manage configurations, and inspect client rosters</p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={orgFilter}
                onChange={(e) => setOrgFilter(e.target.value as OrgFilter)}
                className="border border-gray-300 rounded-lg text-xs font-semibold px-3 py-2 bg-white text-gray-700 shadow-sm focus:outline-none focus:ring-1 focus:ring-[#0c1e40]"
              >
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="rejected">Rejected</option>
                <option value="all">All</option>
              </select>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase">Organization</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase">Billing email</th>
                  <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {insurers.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center text-sm text-gray-500">
                      No organizations found.
                    </td>
                  </tr>
                ) : (
                  insurers.map((org) => (
                    <tr key={org.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <button
                          type="button"
                          onClick={() => loadOrgDetail(org.id)}
                          className="font-bold text-[#0c1e40] hover:text-blue-600 text-left text-sm"
                        >
                          {org.name}
                        </button>
                        <div className="text-xs text-gray-400">Created {adminService.formatDate(org.created_at)}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{org.billing_email || '—'}</td>
                      <td className="px-6 py-4">
                        {org.status === 'active' && (
                          <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                            Active
                          </span>
                        )}
                        {org.status === 'pending' && (
                          <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100">
                            Pending
                          </span>
                        )}
                        {org.status === 'suspended' && (
                          <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-100">
                            Suspended
                          </span>
                        )}
                        {org.status === 'rejected' && (
                          <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                            Rejected
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right space-x-2">
                        <button
                          type="button"
                          onClick={() => loadOrgDetail(org.id)}
                          className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          View detail
                        </button>
                        {org.status === 'pending' && (
                          <>
                            <button
                              type="button"
                              disabled={actionId === org.id}
                              onClick={() => approve(org.id)}
                              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              disabled={actionId === org.id}
                              onClick={() => setRejectingId(org.id)}
                              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {org.status === 'active' && (
                          <button
                            type="button"
                            disabled={actionId === org.id}
                            onClick={() => handleOpenOrgStatusConfirm(org.id, org.name, 'suspended')}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                          >
                            Suspend
                          </button>
                        )}
                        {org.status === 'suspended' && (
                          <button
                            type="button"
                            disabled={actionId === org.id}
                            onClick={() => handleOpenOrgStatusConfirm(org.id, org.name, 'active')}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#0c1e40] text-white hover:bg-slate-800 disabled:opacity-50"
                          >
                            Reactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Org Detail Panel */}
          {selectedOrgId !== null && detailLoading && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center text-sm text-gray-500">
              Loading organization details...
            </div>
          )}
          {selectedOrgId !== null && !detailLoading && orgDetail && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-6">
              <div className="flex items-start justify-between gap-4 pb-4 border-b border-gray-100">
                <div>
                  <h3 className="text-xl font-bold text-[#0c1e40]">{orgDetail.org.name}</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {orgDetail.org.billing_email || 'No billing email'} &bull; {orgDetail.org.status}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => adminService.downloadOrgClientsCsv(orgDetail.org.id)}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Export clients CSV
                  </button>
                  <button
                    type="button"
                    onClick={() => adminService.downloadPartnerLedgerCsv(orgDetail.org.id)}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Export ledger CSV
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedOrgId(null);
                      setOrgDetail(null);
                    }}
                    className="text-gray-400 hover:text-gray-600 text-lg font-bold ml-2"
                  >
                    &times;
                  </button>
                </div>
              </div>

              {/* Edit Organization Form */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  saveOrgEdit();
                }}
                className="bg-slate-50 border border-gray-200 rounded-xl p-4 space-y-3 max-w-xl"
              >
                <h4 className="text-xs font-bold text-[#0c1e40] uppercase tracking-wider">Edit Organization Info</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <input
                    type="text"
                    required
                    value={editForm.name}
                    onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-xs bg-white"
                    placeholder="Organization name"
                  />
                  <input
                    type="email"
                    value={editForm.billing_email}
                    onChange={(e) => setEditForm((f) => ({ ...f, billing_email: e.target.value }))}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-xs bg-white"
                    placeholder="Billing email"
                  />
                  <input
                    type="email"
                    value={editForm.invite_contact_email}
                    onChange={(e) => setEditForm((f) => ({ ...f, invite_contact_email: e.target.value }))}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-xs bg-white"
                    placeholder="Reply-to email"
                  />
                </div>
                <button
                  type="submit"
                  disabled={editSaving}
                  className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-[#0c1e40] text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  {editSaving ? 'Saving…' : 'Save changes'}
                </button>
              </form>

              {/* Org Totals */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="rounded-xl border border-gray-200 p-4 bg-white">
                  <p className="text-xs uppercase font-semibold text-gray-500">Clients</p>
                  <p className="text-xl font-bold text-[#0c1e40] mt-1">{orgDetail.clients.length}</p>
                </div>
                <div className="rounded-xl border border-gray-200 p-4 bg-white">
                  <p className="text-xs uppercase font-semibold text-gray-500">Outstanding</p>
                  <p className="text-xl font-bold text-[#0c1e40] mt-1">
                    {formatCents(orgDetail.totals.outstanding_amount_cents)}
                  </p>
                </div>
                <div className="rounded-xl border border-gray-200 p-4 bg-white">
                  <p className="text-xs uppercase font-semibold text-gray-500">Paid</p>
                  <p className="text-xl font-bold text-[#0c1e40] mt-1">
                    {formatCents(orgDetail.totals.paid_amount_cents)}
                  </p>
                </div>
                <div className="rounded-xl border border-gray-200 p-4 bg-white">
                  <p className="text-xs uppercase font-semibold text-gray-500">Ledger entries</p>
                  <p className="text-xl font-bold text-[#0c1e40] mt-1">{orgDetail.totals.total_entries}</p>
                </div>
              </div>

              {/* Team Members List */}
              <div>
                <button
                  type="button"
                  onClick={() => setTeamExpanded((expanded) => !expanded)}
                  className="flex items-center gap-1.5 text-sm font-bold text-[#0c1e40] mb-3 hover:text-blue-600"
                  aria-expanded={teamExpanded}
                >
                  {teamExpanded ? (
                    <ChevronDownIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  )}
                  <span>Team ({orgDetail.team.length})</span>
                </button>
                {teamExpanded && (
                  <div className="overflow-hidden rounded-xl border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2.5 text-left font-semibold text-gray-500 uppercase">Name</th>
                          <th className="px-4 py-2.5 text-left font-semibold text-gray-500 uppercase">Email</th>
                          <th className="px-4 py-2.5 text-left font-semibold text-gray-500 uppercase">Role</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {orgDetail.team.map((member) => (
                          <tr key={member.id}>
                            <td className="px-4 py-2.5 font-bold text-[#0c1e40]">{member.full_name || '—'}</td>
                            <td className="px-4 py-2.5 text-gray-600">{member.email}</td>
                            <td className="px-4 py-2.5">
                              <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#0c1e40] text-white">
                                {member.org_role === 'org_admin' ? 'Admin' : 'Member'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Clients List */}
              <div>
                <button
                  type="button"
                  onClick={() => setClientsExpanded((expanded) => !expanded)}
                  className="flex items-center gap-1.5 text-sm font-bold text-[#0c1e40] mb-3 hover:text-blue-600"
                  aria-expanded={clientsExpanded}
                >
                  {clientsExpanded ? (
                    <ChevronDownIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  )}
                  <span>Clients ({orgDetail.clients.length})</span>
                </button>
                {clientsExpanded && (
                  <div className="overflow-hidden rounded-xl border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2.5 text-left font-semibold text-gray-500 uppercase">Client</th>
                          <th className="px-4 py-2.5 text-left font-semibold text-gray-500 uppercase">Credits</th>
                          <th className="px-4 py-2.5 text-left font-semibold text-gray-500 uppercase">Exercises</th>
                          <th className="px-4 py-2.5 text-right font-semibold text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 bg-white">
                        {orgDetail.clients.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="px-4 py-6 text-center text-gray-500">
                              No clients linked yet.
                            </td>
                          </tr>
                        ) : (
                          orgDetail.clients.map((client) => (
                            <tr key={client.id}>
                              <td className="px-4 py-2.5 font-bold text-[#0c1e40]">{client.email}</td>
                              <td className="px-4 py-2.5 font-semibold text-gray-700">{client.credits_remaining}</td>
                              <td className="px-4 py-2.5 font-semibold text-gray-700">{client.exercises_used}</td>
                              <td className="px-4 py-2.5 text-right space-x-2">
                                <button
                                  type="button"
                                  disabled={clientActionId === client.id || orgDetail.org.status !== 'active'}
                                  onClick={() => grantClientCredit(orgDetail.org.id, client.id)}
                                  className="px-2.5 py-1 text-xs font-semibold rounded border border-blue-600 text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                                >
                                  Grant
                                </button>
                                {client.credits_remaining > 0 && (
                                  <button
                                    type="button"
                                    disabled={clientActionId === client.id}
                                    onClick={() => setRevokeTarget({ orgId: orgDetail.org.id, client })}
                                    className="px-2.5 py-1 text-xs font-semibold rounded border border-red-600 text-red-600 hover:bg-red-50 disabled:opacity-50"
                                  >
                                    Revoke
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ───────────── ONBOARD INSURER MODAL ───────────── */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full border border-gray-200 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-[#0c1e40]">Onboard Insurance Partner</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Provision an insurer organization and assign its primary administrator.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex items-center justify-center"
              >
                &times;
              </button>
            </div>

            <form className="p-6 space-y-4" onSubmit={createInsurerOrg}>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Organization Name <span className="text-rose-500">*</span>
                </label>
                <input
                  required
                  value={createForm.org_name}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, org_name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg text-xs px-3 py-2 bg-slate-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#0c1e40] text-gray-900"
                  placeholder="e.g. Coalition, Chubb, Cowbell Cyber"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Billing Email <span className="text-rose-500">*</span>
                </label>
                <input
                  required
                  type="email"
                  value={createForm.billing_email}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, billing_email: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg text-xs px-3 py-2 bg-slate-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#0c1e40] text-gray-900"
                  placeholder="billing@insurer.com"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Admin Full Name</label>
                  <input
                    value={createForm.admin_full_name}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, admin_full_name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg text-xs px-3 py-2 bg-slate-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#0c1e40] text-gray-900"
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Admin Work Email <span className="text-rose-500">*</span>
                  </label>
                  <input
                    required
                    type="email"
                    value={createForm.admin_email}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, admin_email: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg text-xs px-3 py-2 bg-slate-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#0c1e40] text-gray-900"
                    placeholder="admin@insurer.com"
                  />
                </div>
              </div>

              <div className="p-3.5 bg-slate-50 rounded-xl border border-gray-200 space-y-2.5">
                <p className="text-xs font-semibold text-gray-800">Admin Activation Method</p>
                <label className="flex items-start gap-2.5 text-xs text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="admin_sign_in"
                    checked={createForm.use_magic_link}
                    onChange={() =>
                      setCreateForm((prev) => ({
                        ...prev,
                        use_magic_link: true,
                        admin_password: '',
                      }))
                    }
                    className="mt-0.5 text-[#0c1e40] focus:ring-[#0c1e40]"
                  />
                  <span>
                    <strong className="text-gray-900 font-semibold">Magic link (recommended):</strong> Sends a secure invitation link.
                  </span>
                </label>
                <label className="flex items-start gap-2.5 text-xs text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="admin_sign_in"
                    checked={!createForm.use_magic_link}
                    onChange={() => setCreateForm((prev) => ({ ...prev, use_magic_link: false }))}
                    className="mt-0.5 text-[#0c1e40] focus:ring-[#0c1e40]"
                  />
                  <span>
                    <strong className="text-gray-900 font-semibold">Set initial password:</strong> Define a temporary password.
                  </span>
                </label>
                {!createForm.use_magic_link && (
                  <div className="pt-2">
                    <input
                      type="password"
                      required={!createForm.use_magic_link}
                      value={createForm.admin_password}
                      onChange={(e) => setCreateForm((prev) => ({ ...prev, admin_password: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg text-xs px-3 py-2 bg-white text-gray-900"
                      placeholder="Enter initial password (min. 8 characters)"
                      minLength={8}
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  id="auto_approve_checkbox"
                  type="checkbox"
                  checked={createForm.auto_approve}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, auto_approve: e.target.checked }))}
                  className="rounded border-gray-300 text-[#0c1e40] focus:ring-[#0c1e40]"
                />
                <label htmlFor="auto_approve_checkbox" className="text-xs font-medium text-gray-700 cursor-pointer">
                  Auto-approve immediately (skips pending review stage)
                </label>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 text-xs font-semibold text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-4 py-2 text-xs font-bold bg-[#991b1b] hover:bg-[#7f1d1d] text-white rounded-lg transition-colors shadow-sm disabled:opacity-50 flex items-center space-x-1.5"
                >
                  {creating && (
                    <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  )}
                  <span>{creating ? 'Onboarding…' : 'Onboard Partner'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ───────────── TOKEN NAMES & TIERS MODAL ───────────── */}
      {showTokenModal && (
        <ManagePartnerTokensModal
          isOpen={showTokenModal}
          onClose={() => setShowTokenModal(false)}
        />
      )}

      {/* ───────────── REVOKE MODAL ───────────── */}
      {revokeTarget && (
        <RevokeTokenModal
          isOpen={!!revokeTarget}
          client={revokeTarget.client}
          onClose={() => setRevokeTarget(null)}
          onConfirm={async () => {
            const res = await adminService.revokeClientCredit(
              revokeTarget.orgId,
              revokeTarget.client.id
            );
            return res.message;
          }}
          onRevoked={async (msg) => {
            setMessage(msg || 'Token revoked.');
            setRevokeTarget(null);
            if (selectedOrgId) {
              await loadOrgDetail(selectedOrgId);
            }
            await load();
          }}
          onError={(err) => setError(err)}
        />
      )}

      {/* ───────────── STATUS CONFIRM MODAL ───────────── */}
      {orgStatusConfirm.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-[#0c1e40]">
              {orgStatusConfirm.status === 'suspended' ? 'Suspend organization?' : 'Reactivate organization?'}
            </h3>
            <p className="text-sm text-gray-600 mt-2">
              {orgStatusConfirm.status === 'suspended' ? (
                <>
                  Suspend <span className="font-semibold text-gray-900">{orgStatusConfirm.orgName}</span>? Members will lose access until reactivated.
                </>
              ) : (
                <>
                  Reactivate <span className="font-semibold text-gray-900">{orgStatusConfirm.orgName}</span>? Members will regain access.
                </>
              )}
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOrgStatusConfirm({ open: false, orgId: null, orgName: '', status: null })}
                disabled={actionId !== null}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmOrgStatus}
                disabled={actionId !== null}
                className={`px-4 py-2 text-sm font-semibold rounded-lg text-white disabled:opacity-50 ${
                  orgStatusConfirm.status === 'suspended'
                    ? 'bg-amber-600 hover:bg-amber-700'
                    : 'bg-[#0c1e40] hover:bg-slate-800'
                }`}
              >
                {actionId !== null ? 'Updating…' : orgStatusConfirm.status === 'suspended' ? 'Suspend' : 'Reactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ───────────── REJECT APPLICATION MODAL ───────────── */}
      {rejectingId !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-[#0c1e40] mb-2">Reject application</h3>
            <p className="text-sm text-gray-600 mb-4">The applicant will receive an email notification.</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Optional reason for rejection"
              className="w-full border border-gray-300 rounded-lg text-sm px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-[#0c1e40]"
              rows={3}
            />
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => {
                  setRejectingId(null);
                  setRejectReason('');
                }}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={actionId === rejectingId}
                onClick={() => reject(rejectingId)}
                className="px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Reject application
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ───────────── LEDGER ACTION MODAL ───────────── */}
      {ledgerAction !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <form onSubmit={submitLedgerAction} className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-base font-bold text-[#0c1e40]">
              Update ledger #{ledgerAction.id} &rarr; {ledgerAction.status}
            </h3>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Invoice reference</label>
              <input
                type="text"
                value={ledgerAction.invoice_reference}
                onChange={(e) =>
                  setLedgerAction((prev) => prev && { ...prev, invoice_reference: e.target.value })
                }
                placeholder="e.g. INV-2026-001"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Audit note</label>
              <input
                type="text"
                value={ledgerAction.note}
                onChange={(e) =>
                  setLedgerAction((prev) => prev && { ...prev, note: e.target.value })
                }
                placeholder="Reason or check number"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setLedgerAction(null)}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-[#0c1e40] text-white hover:bg-slate-800"
              >
                Save
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default InsurerManagement;
